import React from 'react';
import { Box, Card, CardContent, Typography, Stack, Divider } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PlaceIcon from '@mui/icons-material/Place';
import { format, parseISO } from 'date-fns';
import { CalendarEvent } from '../types/event';

interface EventCardProps {
    event: CalendarEvent;
    index: number;
}

const EventCard = ({ event, index }: EventCardProps) => {
    const startDate = parseISO(event.startDateTime);
    const endDate = parseISO(event.endDateTime);

    const formatTime = (date: Date) => format(date, "h:mm a");
    const formatDate = (date: Date) => format(date, "EEEE, MMMM d, yyyy");

    return (
        <Card
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 10,
                    '& .title-text': {
                        color: 'primary.main'
                    }
                },
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                width: '100%'
            }}
        >
            {/* Date Badge */}
            <Box
                sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    px: 2,
                    py: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: { xs: '100%', sm: 110 },
                    textAlign: 'center'
                }}
            >
                <Typography
                    variant="overline"
                    sx={{
                        opacity: 0.9,
                        fontWeight: 700,
                        lineHeight: 1,
                        mb: 0.5,
                        letterSpacing: '0.1em'
                    }}
                >
                    {format(startDate, 'MMM')}
                </Typography>
                <Typography
                    variant="h3"
                    fontWeight="800"
                    sx={{ lineHeight: 1, my: 0.5 }}
                >
                    {format(startDate, 'd')}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{ opacity: 0.8, fontWeight: 500 }}
                >
                    {format(startDate, 'EEE')}
                </Typography>
            </Box>

            {/* Content */}
            <CardContent sx={{ flex: 1, p: { xs: 3, sm: 4 }, '&:last-child': { pb: { xs: 3, sm: 4 } } }}>
                <Typography
                    className="title-text"
                    variant="h5"
                    component="h3"
                    fontWeight="700"
                    gutterBottom
                    sx={{
                        transition: 'color 0.2s ease',
                        mb: 2
                    }}
                >
                    {event.title}
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        mb: 3,
                        lineHeight: 1.6,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                    }}
                >
                    {event.description}
                </Typography>

                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={{ xs: 2, md: 4 }}
                    sx={{ mb: event.location ? 3 : 0 }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <AccessTimeIcon color="primary" sx={{ fontSize: 20 }} />
                        <Typography variant="body2" fontWeight="500">
                            {formatTime(startDate)} – {formatTime(endDate)}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CalendarMonthIcon color="primary" sx={{ fontSize: 20 }} />
                        <Typography variant="body2" fontWeight="500">
                            {formatDate(startDate)}
                        </Typography>
                    </Box>
                </Stack>

                {event.location && (
                    <>
                        <Divider sx={{ my: 2.5, opacity: 0.6 }} />
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <PlaceIcon color="secondary" sx={{ fontSize: 20, mt: 0.2 }} />
                            <Typography variant="body2" color="text.secondary">
                                {event.location}
                            </Typography>
                        </Box>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default EventCard;
